import { Dispatch, useEffect, useRef } from 'react';
import api, { ProactiveResponse } from '../API/api';
import { AppAction, AppState, PROACTIVE_TIMER_PHASE } from '../Components/App/appReducer';
import { ChatMessage } from '../Components/Shared/API/ApiTypes';
import { EdullaConfig } from '../config/configType';
import { debug } from './logging';
import { postToLauncher } from './postToLauncher';

// Task 4.8: render the nudge, forward it to the launcher, log the impression.
const showNudge = (
    response: ProactiveResponse,
    state: AppState,
    cfg: EdullaConfig,
    dispatch: Dispatch<AppAction>
): void => {
    const msg: ChatMessage = {
        content: response.answer!,
        direction: 1,
        time: new Date(),
        proactive: true,
        proactiveMessageId: response.proactiveMessageId,
    };
    dispatch({ type: 'renderProactiveMessage', message: msg });

    // Forward the proactive message to the launcher so it can show
    // a teaser when the chat is minimized (the iframe is hidden then,
    // so the teaser must live outside it). The launcher decides
    // visibility based on its own minimized state.
    postToLauncher({
        isEdulla: true,
        action: 'proactiveMessage',
        data: {
            text: response.answer,
            proactiveMessageId: response.proactiveMessageId ?? null,
        },
    });

    // Task 6.1: Log ProactiveShown
    try {
        api.logEvent.logEvent({
            eventtype: 'ProactiveShown',
            eventvalue: response.proactiveMessageId ?? null,
            indexname: cfg.IndexName,
            sessionid: state.sessionid,
            pageurl: state.pageUrl || '',
        });
    } catch (_) {}
};

// Tasks 4.9/4.10: scheduling is independent of whether a nudge was rendered —
// re-arm when the server offers a next delay, otherwise the campaign is over.
const nextStep = (response: ProactiveResponse): AppAction =>
    response.nextDelaySeconds
        ? { type: 'rearmProactiveTimer', nextDelaySeconds: response.nextDelaySeconds }
        : { type: 'doneProactive' };

// Tasks 4.8–4.10: the firing flow — ask the backend for a nudge and apply the
// outcome. Plain async function, no React: the hook decides when to run it
// and owns cancellation (the isCancelled callback reads the effect's cleanup
// flag, so a fire resolving after unmount/re-render dispatches nothing).
const executeProactiveFire = async (
    state: AppState,
    cfg: EdullaConfig,
    proactiveAttemptId: string,
    dispatch: Dispatch<AppAction>,
    isCancelled: () => boolean
): Promise<void> => {
    try {
        const response = await api.proactive.fire({
            sessionid: state.sessionid,
            index: cfg.IndexName,
            proactiveAttemptId,
            context: state.messages.filter((m: ChatMessage) => !m.temp),
            current_url: state.pageUrl || undefined,
        });
        if (isCancelled()) return;

        if (response.send && response.answer) {
            showNudge(response, state, cfg, dispatch);
        }
        dispatch(nextStep(response));
    } catch (_) {
        // Network error: go back to ARMED keeping same proactiveAttemptId for retry
        if (!isCancelled()) {
            dispatch({ type: 'suppressProactiveTimer' });
        }
    }
};

export function useProactiveTimer(
    appState: AppState,
    config: EdullaConfig,
    dispatch: Dispatch<AppAction>
) {
    const hasSentMessage = appState.messages.some((m: ChatMessage) => m.direction === 0);
    const isStreaming = appState.messages.some((m: ChatMessage) => m.temp);
    const phase = appState.proactiveTimerPhase ?? PROACTIVE_TIMER_PHASE.idle;

    const lastUserMsg = [...appState.messages].filter((m: ChatMessage) => m.direction === 0).pop();
    const idleRef = Math.max(
        lastUserMsg ? new Date(lastUserMsg.time).getTime() : 0,
        appState.proactiveLastActivityAt ?? 0
    );

    const delayMs =
        (appState.proactiveCurrentDelaySeconds ?? config.ProactiveFirstDelaySeconds ?? 0) * 1000;
    const enabled = !!(hasSentMessage && config.ProactiveEnabled && delayMs > 0);

    debug('proactive', { phase, enabled, hasSentMessage, ProactiveEnabled: config.ProactiveEnabled, delayMs, idleElapsed: Date.now() - idleRef });

    // Keep refs so polling closure always reads latest values without recreating the interval
    const isStreamingRef = useRef(isStreaming);
    const idleRefRef = useRef(idleRef);
    const delayMsRef = useRef(delayMs);
    useEffect(() => { isStreamingRef.current = isStreaming; });
    useEffect(() => { idleRefRef.current = idleRef; });
    useEffect(() => { delayMsRef.current = delayMs; });

    // Snapshot refs for the FIRING effect (read latest at fire time)
    const appStateRef = useRef(appState);
    const configRef = useRef(config);
    useEffect(() => { appStateRef.current = appState; });
    useEffect(() => { configRef.current = config; });

    // Task 4.1: Arm timer when enabled and IDLE
    useEffect(() => {
        if (!enabled || phase !== PROACTIVE_TIMER_PHASE.idle) return;
        dispatch({ type: 'armProactiveTimer' });
    }, [enabled, phase, dispatch]);

    // Tasks 4.2–4.5, 4.7: Poll for idle threshold; visibilitychange; streaming suppression
    useEffect(() => {
        if (!enabled || phase !== PROACTIVE_TIMER_PHASE.armed) return;

        const checkAndFire = () => {
            if (isStreamingRef.current) return;
            const elapsed = Date.now() - idleRefRef.current;
            debug('proactive:poll', { elapsed, delayMs: delayMsRef.current, willFire: elapsed >= delayMsRef.current });
            if (elapsed >= delayMsRef.current) {
                dispatch({ type: 'fireProactiveTimer' });
            }
        };

        // Immediate check on (re)mount or phase change — handles navigation re-arm (4.5)
        checkAndFire();

        // Task 4.4: Short polling interval instead of a single long setTimeout
        const interval = setInterval(checkAndFire, 5000);

        // Task 4.3: visibilitychange — fire immediately if threshold passed while hidden
        const onVisibility = () => {
            if (document.visibilityState === 'visible') checkAndFire();
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [enabled, phase, dispatch]);

    // Tasks 4.8–4.10: Execute API call when FIRING
    useEffect(() => {
        if (phase !== PROACTIVE_TIMER_PHASE.firing) return;

        const state = appStateRef.current;
        const cfg = configRef.current;
        if (!state.proactiveAttemptId || !cfg.IndexName) {
            dispatch({ type: 'suppressProactiveTimer' });
            return;
        }

        let cancelled = false;
        executeProactiveFire(state, cfg, state.proactiveAttemptId, dispatch, () => cancelled);
        return () => {
            cancelled = true;
        };
    }, [phase, dispatch]);
}
